import { Component, Input, OnInit } from '@angular/core';

import * as moment from 'moment';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AccountService } from '../../../../../../services/account/account.service';
import { generateCsvDownload, getISO8601DateString, propExists } from '../../../../../../utils';
import {
  getWaitlistEntryStatusDisplay,
  ScheduledEvent,
  WaitlistEntry
} from '../../../../../../models/scheduled-event';
import { Customer, CustomerProfileDetails } from '../../../../../../models/customer';

const COLUMN_NAME_ID = 'Waitlist Entry ID';
const COLUMN_NAME_FIRST_NAME = 'Participant First Name';
const COLUMN_NAME_LAST_NAME = 'Participant Last Name';
const COLUMN_NAME_BIRTHDAY = 'Participant Birthday';
const COLUMN_NAME_AGE = 'Participant Age';
const COLUMN_NAME_NOTIFICATION_EMAIL = 'Notification Email';
const COLUMN_NAME_STATUS = 'Waitlist Status';
const COLUMN_NAME_PARTICIPANT_CUSTOMER_ID = 'Participant Customer ID';
const COLUMN_NAME_BOOKING_CUSTOMER_ID = 'Booking Customer ID';
const COLUMN_NAME_BOOKING_CUSTOMER_FIRST_NAME = 'Booking Customer First Name';
const COLUMN_NAME_BOOKING_CUSTOMER_LAST_NAME = 'Booking Customer Last Name';
const COLUMN_NAME_BOOKING_CUSTOMER_EMAIL = 'Booking Customer Email';
const COLUMN_NAME_BOOKING_CUSTOMER_TELEPHONE = 'Booking Customer Telephone';
const COLUMN_NAME_CREATED_AT = 'Created At';
const COLUMN_NAME_LAST_ACTIVITY_AT = 'Last Activity At';
const COLUMN_NAME_BOOKING_DEADLINE = 'Booking Deadline';

@Component({
  selector: 'app-export-event-waitlist-modal',
  templateUrl: './export-event-waitlist-modal.component.html',
  styleUrls: ['./export-event-waitlist-modal.component.css']
})
export class ExportEventWaitlistModalComponent implements OnInit {

  @Input() waitlistEntries: WaitlistEntry[];
  @Input() customers: {[id: number]: Customer};
  @Input() customerProfileDetails: {[id: number]: CustomerProfileDetails};
  @Input() savedCallback: () => undefined;

  public columnNames = [
    COLUMN_NAME_ID,
    COLUMN_NAME_FIRST_NAME,
    COLUMN_NAME_LAST_NAME,
    COLUMN_NAME_BIRTHDAY,
    COLUMN_NAME_AGE,
    COLUMN_NAME_NOTIFICATION_EMAIL,
    COLUMN_NAME_STATUS,
    COLUMN_NAME_PARTICIPANT_CUSTOMER_ID,
    COLUMN_NAME_BOOKING_CUSTOMER_ID,
    COLUMN_NAME_BOOKING_CUSTOMER_FIRST_NAME,
    COLUMN_NAME_BOOKING_CUSTOMER_LAST_NAME,
    COLUMN_NAME_BOOKING_CUSTOMER_EMAIL,
    COLUMN_NAME_BOOKING_CUSTOMER_TELEPHONE,
    COLUMN_NAME_CREATED_AT,
    COLUMN_NAME_LAST_ACTIVITY_AT,
    COLUMN_NAME_BOOKING_DEADLINE
  ];

  public defaultDownloadFileName: string;
  public downloadFileName: string;
  // Whether each columnNames item has been selected for inclusion in the
  // download.
  public selectedColumns: {[key: string]: boolean};

  constructor(
    public accountService: AccountService,
    public activeModal: NgbActiveModal
  ) {}

  ngOnInit() {
    this.defaultDownloadFileName = 'Event_Waitlist_Export_' + getISO8601DateString(new Date()) + '.csv';
    this.downloadFileName = this.defaultDownloadFileName;

    this.selectedColumns = {};
    for (const columnName of this.columnNames) {
      this.selectedColumns[columnName] = false;
    }
  }

  public noColumnsAreSelected() {
    for (const columnName of this.columnNames) {
      if (this.selectedColumns[columnName]){
        return false;
      }
    }

    return true;
  }

  public allColumnsAreSelected() {
    for (const columnName of this.columnNames) {
      if (!this.selectedColumns[columnName]){
        return false;
      }
    }

    return true;
  }

  public setAllColumnsSelected(isSelected: boolean) {
    this.columnNames.forEach(columnName => {
      this.selectedColumns[columnName] = isSelected;
    });
  }

  public handleSubmit() {
    if (!this.downloadFileName) {
      this.downloadFileName = this.defaultDownloadFileName;
    }

    const selectedColumnsList = this.getSelectedColumnsList();
    const content = [selectedColumnsList];
    for (const waitlistEntry of this.waitlistEntries) {
      const thisContentRow: string[] = [];
      for (const selectedColumnName of selectedColumnsList) {
        thisContentRow.push(
          this.getContentItem(waitlistEntry, selectedColumnName)
        );
      }
      content.push(thisContentRow);
    }

    generateCsvDownload(content, this.downloadFileName);
  }

  /**
   * Get the selected columns as a list.
   */
  private getSelectedColumnsList(): string[] {
    const selectedColumnsList: string[] = [];
    for (const columnName of this.columnNames) {
      if (this.selectedColumns[columnName]) {
        selectedColumnsList.push(columnName);
      }
    }
    return selectedColumnsList;
  }

  private getContentItem(entry: WaitlistEntry,
                         selectedColumnName: string): string {
    switch (selectedColumnName) {
      case COLUMN_NAME_ID:
        return entry.id;
      case COLUMN_NAME_FIRST_NAME:
        return entry.first_name;
      case COLUMN_NAME_LAST_NAME:
        return entry.last_name;
      case COLUMN_NAME_BIRTHDAY:
        return entry.birthday;
      case COLUMN_NAME_AGE:
        const birthday = moment(entry.birthday, 'YYYY-MM-DD');
        return moment().diff(birthday, 'years').toString();
      case COLUMN_NAME_NOTIFICATION_EMAIL:
        return entry.notification_email;
      case COLUMN_NAME_STATUS:
        return getWaitlistEntryStatusDisplay(entry.status);
      case COLUMN_NAME_PARTICIPANT_CUSTOMER_ID:
        if (!propExists(entry.participant_customer_id)) {
          return null;
        }
        return entry.participant_customer_id.toString();
      case COLUMN_NAME_BOOKING_CUSTOMER_ID:
        if (!propExists(entry.booking_customer_id)) {
          return null;
        }
        return entry.booking_customer_id.toString();
      case COLUMN_NAME_BOOKING_CUSTOMER_FIRST_NAME:
        return entry.booking_customer_first_name;
      case COLUMN_NAME_BOOKING_CUSTOMER_LAST_NAME:
        return entry.booking_customer_last_name;
      case COLUMN_NAME_BOOKING_CUSTOMER_EMAIL:
        return entry.booking_customer_email;
      case COLUMN_NAME_BOOKING_CUSTOMER_TELEPHONE:
        return entry.booking_customer_telephone;
      case COLUMN_NAME_CREATED_AT:
        return entry.waiting_since;
      case COLUMN_NAME_LAST_ACTIVITY_AT:
        return entry.updated_at;
      case COLUMN_NAME_BOOKING_DEADLINE:
        return entry.booking_deadline;
      default:
        return 'Unknown Column';
    }
  }

}
